import { Property, type User } from '@kalos/kalos-rpc';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@kalos/ui';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { type FilterDimension } from '../../../../components/FilterSheet';
import { ListResultsPhone } from '../../../../components/ListPhone';
import { currentSearchHref } from '../../../../components/searchReturnTo';
import { queryKeys } from '../../../../hooks/react-query/constants';
import { type PropertyFilter } from '../../../../hooks/react-query/usePropertiesQuery';
import { useBatchUserQuery } from '../../../../hooks/react-query/useUserQuery';
import { PropertyClientService } from '../../../../tools/helpers';
import { type ListSort } from '../listState';
import { PhoneSearchLayout } from '../PhoneSearchLayout';
import { SearchResultRow } from '../SearchResultRow';
import { useSearchPages } from '../useSearchPages';
import PropertySearchForm from './PropertySearchForm';
import { defaultPropertiesSearchValues, type PropertiesSearchScheme } from './utils';

const dimensions: FilterDimension<PropertiesSearchScheme>[] = [
  { id: 'address', label: 'Address', fields: ['address'], summary: (v) => v.address ?? '' },
  {
    id: 'subdivision',
    label: 'Subdivision',
    fields: ['subdivision'],
    summary: (v) => v.subdivision ?? '',
  },
  { id: 'city', label: 'City', fields: ['city'], summary: (v) => v.city ?? '' },
  { id: 'zip', label: 'Zip Code', fields: ['zip'], summary: (v) => v.zip ?? '' },
];

/** The table's sortable columns, each way. Address, A to Z, is the default. */
const sortOptions = [
  ['address', 'Address'],
  ['subdivision', 'Subdivision'],
  ['city', 'City'],
  ['zip', 'Zip Code'],
].flatMap(([id, label]) => [
  { value: `${id}.asc`, label: `${label}, A to Z` },
  { value: `${id}.desc`, label: `${label}, Z to A` },
]);

/** The owner as the table shows it: the business name, else first and last name. */
const ownerName = (user: User | undefined) => {
  if (!user) return '';
  const business = user.businessname?.trim();
  if (business) return business;
  return [user.firstname, user.lastname]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
};

/**
 * Properties on phones: the box searches the Address field, so every other filter still applies
 * while it has text. One row per property: it opens the property's page, or, in a picker,
 * selects it.
 */
export const PropertiesSearchPhone = ({
  form,
  request,
  sort,
  onSortChange,
  onSelect,
  exceptionId,
  onDelete,
}: {
  form: UseFormReturn<PropertiesSearchScheme>;
  /** The table's request; each page sets its own number. */
  request: PropertyFilter;
  sort: ListSort | null;
  onSortChange: (sort: ListSort | null) => void;
  /** Picker mode: the row selects the property instead of opening it. */
  onSelect?: (property: Property) => void;
  /** The property already open, which can't be picked. */
  exceptionId?: number;
  onDelete?: (property: Property) => void;
}) => {
  const navigate = useNavigate();
  const pages = useSearchPages({
    queryKey: [
      queryKeys.properties.root,
      queryKeys.properties.list,
      'pages',
      queryKeys.properties.getHash({ ...request, pageNumber: undefined }),
    ],
    fetchPage: (pageNumber) =>
      PropertyClientService.BatchGet(Property.create({ ...request, pageNumber })),
  });

  // Owners for every loaded row, in one request, as the table looks them up.
  const ownerIds = useMemo(
    () => [...new Set(pages.rows.map((property) => property.userId).filter(Boolean))].join(','),
    [pages.rows],
  );
  const ownersQuery = useBatchUserQuery({
    filters: { userIdList: ownerIds, overrideLimit: true },
    enabled: ownerIds.length > 0,
  });
  const owners = useMemo(
    () => new Map((ownersQuery.data?.results ?? []).map((user) => [user.id, user])),
    [ownersQuery.data],
  );

  return (
    <div className="flex flex-col gap-3 px-4 pb-4">
      <PhoneSearchLayout
        form={form}
        searchField="address"
        searchPlaceholder="Search by address"
        defaultFilters={defaultPropertiesSearchValues}
        dimensions={dimensions}
        sheetTitle="Filter properties"
        renderFields={() => <PropertySearchForm inSheet />}
        sortOptions={sortOptions}
        sort={sort}
        defaultSort={{ id: 'address', desc: false }}
        onSortChange={onSortChange}
      >
        {({ isFiltered, clearFilters }) => (
          <ListResultsPhone
            itemLabel="properties"
            emptyTitle="No results."
            rowCount={pages.rows.length}
            totalCount={pages.totalCount}
            isPending={pages.isPending}
            isFetching={pages.isFetching}
            isError={pages.isError}
            isFetchingNextPage={pages.isFetchingNextPage}
            hasNextPage={pages.hasNextPage}
            onLoadMore={() => void pages.fetchNextPage()}
            onRetry={() => void pages.refetch()}
            isFiltered={isFiltered}
            onClearFilters={clearFilters}
          >
            {pages.rows.map((property) => {
              const isCurrent = !!exceptionId && property.id === exceptionId;
              // Some records have no street address; the city and zip still say where it is.
              const address =
                property.address?.trim() ||
                [property.city, property.zip].filter(Boolean).join(' ') ||
                'No address on record';
              return (
                <SearchResultRow
                  key={property.id}
                  title={address}
                  lines={[
                    ownerName(owners.get(property.userId)),
                    [
                      property.subdivision?.trim(),
                      [property.city, property.zip].filter(Boolean).join(' '),
                    ]
                      .filter(Boolean)
                      .join(' · '),
                    isCurrent ? 'Current Property' : '',
                  ]}
                  actionLabel={onSelect ? 'Select' : 'Open property'}
                  hint={onSelect && !isCurrent ? 'Select' : undefined}
                  disabled={isCurrent}
                  // The property's page, as a row click opens it on desktop, carrying this search
                  // so Back returns to it. Editing lives on the page.
                  onSelect={() =>
                    onSelect
                      ? onSelect(property)
                      : navigate({
                          to: '/property-details/$propertyId',
                          params: { propertyId: property.id },
                          search: { returnTo: currentSearchHref() },
                        })
                  }
                  corner={
                    !onSelect && onDelete ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`More actions for ${address}`}
                          >
                            More
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => onDelete(property)}>
                            Delete Property
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : undefined
                  }
                />
              );
            })}
          </ListResultsPhone>
        )}
      </PhoneSearchLayout>
    </div>
  );
};
